import React from 'react';

export const DEFAULT_LIMIT = '40';
export const DEFAULT_STATUS = 'pending_triage';

export const STATUS_OPTIONS = [
  { value: 'pending_triage', label: '분류 대기' },
  { value: 'assigned', label: '담당자 배정' },
  { value: 'in_review', label: '검토 중' },
  { value: 'resolved', label: '해결됨' },
  { value: 'dismissed', label: '검토 제외' },
  { value: 'reopened', label: '재검토' },
  { value: '', label: '전체 상태' },
];

export const LIMIT_OPTIONS = ['20', '40', '80'];

export const STATUS_WRITE_OPTIONS = [
  { value: 'assigned', label: '담당자 배정' },
  { value: 'in_review', label: '검토 중' },
  { value: 'resolved', label: '해결됨' },
  { value: 'dismissed', label: '검토 제외' },
  { value: 'reopened', label: '재검토' },
  { value: 'pending_triage', label: '분류 대기' },
];

export const ACTION_TYPE_OPTIONS = [
  { value: 'safe_close', label: '안전 종료' },
  { value: 'clinician_handoff', label: '임상의 인계' },
  { value: 'patient_contact', label: '환자 연락' },
  { value: 'false_positive', label: '오탐 처리' },
];

export const STATUS_LABELS = Object.fromEntries(
  STATUS_OPTIONS.filter((option) => option.value).map((option) => [option.value, option.label])
);

export const CLOSED_STATUSES = new Set(['resolved', 'dismissed']);

export const RISK_LABELS = {
  high: '높음',
  medium: '중간',
  low: '낮음',
};

export const EVENT_LABELS = {
  created: '케이스 생성',
  assigned: '담당자 배정',
  status_changed: '상태 변경',
  note_created: '노트 작성',
  action_recorded: '조치 기록',
  assistant_draft_decision: '초안 결정',
};

export const ARTIFACT_LABELS = {
  evidence_snapshot: '근거 스냅샷',
  blocked_answer_snapshot: '차단 답변 스냅샷',
  continuity_turn: '대화 연속성',
  report_context: '리포트 컨텍스트',
  wearable_context: '웨어러블 컨텍스트',
  icsn_context: 'iCSN 컨텍스트',
};

export const SNAPSHOT_ERROR_LABELS = {
  evidence: '근거 스냅샷',
  blockedAnswer: '차단 답변 스냅샷',
};

export const DOMAIN_VALUE_LABELS = {
  admin: '관리자',
  operator: '운영자',
  clinician: '임상의',
  service: '서비스',
  system: '시스템',
  normal: '보통',
  high: '높음',
  medium: '중간',
  low: '낮음',
  general_health: '일반 건강',
  chronic_care: '만성질환 관리',
  stopline_hold: '스톱라인 보류',
  'ai-care-coach': 'AI 케어 코치',
  evidence_pipeline_fallback: '근거 파이프라인 폴백',
  required_evidence_unmet: '필수 근거 미충족',
  data_grounded_low_evidence: '데이터 근거 부족',
  filtered_irrelevant: '관련성 부족으로 제외',
  request_error: '요청 오류',
  timeout: '시간 초과',
  fallback: '폴백',
  miss: '근거 없음',
  hit: '근거 있음',
  preview: '미리보기',
  full: '전문',
  available: '사용 가능',
  unavailable: '사용 불가',
  missing: '없음',
  stale: '오래됨',
  fresh: '최신',
  internal: '내부',
  triage: '분류',
  accepted: '채택',
  rejected: '반려',
  review_case_drafts: '검토 초안',
  'review-assistant-agent': '검토 보조 에이전트',
};

export const ACTION_LABELS = Object.fromEntries(
  ACTION_TYPE_OPTIONS.map((option) => [option.value, option.label])
);

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function normalizeIntegerFilter(value) {
  const trimmed = String(value ?? '').trim();
  return /^\d+$/.test(trimmed) ? trimmed : '';
}

export function buildFilterState(searchParams) {
  return {
    status: searchParams.get('status') || DEFAULT_STATUS,
    userNo: normalizeIntegerFilter(searchParams.get('user_no')),
    patientId: normalizeIntegerFilter(searchParams.get('patient_id')),
    limit: searchParams.get('limit') || DEFAULT_LIMIT,
  };
}

export function buildSearchParams(filters) {
  const params = new URLSearchParams();
  const userNo = normalizeIntegerFilter(filters.userNo);
  const patientId = normalizeIntegerFilter(filters.patientId);

  if (filters.status) {
    params.set('status', filters.status);
  }
  if (userNo) {
    params.set('user_no', userNo);
  }
  if (patientId) {
    params.set('patient_id', patientId);
  }
  if (filters.limit && filters.limit !== DEFAULT_LIMIT) {
    params.set('limit', filters.limit);
  }

  return params;
}

export function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  try {
    return DATE_TIME_FORMATTER.format(new Date(value));
  } catch {
    return String(value);
  }
}

export function parseDateMs(value) {
  if (!value) {
    return null;
  }
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

export function formatDuration(ms) {
  if (!Number.isFinite(ms) || ms < 0) {
    return '-';
  }

  const totalMinutes = Math.floor(ms / 60000);
  if (totalMinutes < 1) {
    return '1분 미만';
  }
  if (totalMinutes < 60) {
    return `${totalMinutes}분`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (totalHours < 24) {
    return minutes ? `${totalHours}시간 ${minutes}분` : `${totalHours}시간`;
  }

  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return hours ? `${days}일 ${hours}시간` : `${days}일`;
}

export function formatCaseAge(caseRecord) {
  const startedAt = parseDateMs(caseRecord?.created_at);
  if (startedAt === null) {
    return '-';
  }
  const endedAt = CLOSED_STATUSES.has(caseRecord?.status)
    ? parseDateMs(caseRecord.closed_at || caseRecord.updated_at) || Date.now()
    : Date.now();
  return formatDuration(endedAt - startedAt);
}

export function formatRelativeSync(value) {
  if (!value) {
    return '첫 동기화 대기 중';
  }

  const diffSeconds = Math.max(0, Math.floor((Date.now() - value) / 1000));
  if (diffSeconds < 10) {
    return '방금 동기화됨';
  }
  if (diffSeconds < 60) {
    return `${diffSeconds}초 전 동기화`;
  }
  if (diffSeconds < 3600) {
    return `${Math.floor(diffSeconds / 60)}분 전 동기화`;
  }
  return `${Math.floor(diffSeconds / 3600)}시간 전 동기화`;
}

export function trimText(value, limit = 180) {
  const text = String(value || '').trim();
  if (!text) {
    return '-';
  }
  return text.length > limit ? `${text.slice(0, limit - 1)}...` : text;
}

export function formatDomainValue(value) {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const normalized = String(value);
  return DOMAIN_VALUE_LABELS[normalized] || normalized;
}

export function formatStoplineSummary(value) {
  const summary = String(value || '').trim();
  if (!summary) {
    return '-';
  }

  const match = summary.match(
    /^Stopline hold triggered for intent=([^,]+), risk_level=([^,]+), reason=([^,]+), rag_status=([^,]+), rag_reason=([^.,]+)\.?$/i
  );
  if (!match) {
    return summary;
  }

  const [, intent, riskLevel, reason, ragStatus, ragReason] = match;
  return [
    `질문 의도: ${formatDomainValue(intent)}`,
    `위험도: ${formatRisk(riskLevel)}`,
    `보류 사유: ${formatDomainValue(reason)}`,
    `RAG 상태: ${formatDomainValue(ragStatus)}`,
    `RAG 사유: ${formatDomainValue(ragReason)}`,
  ].join(' / ');
}

export function resolveMetadataValue(metadata, key) {
  if (!metadata || typeof metadata !== 'object') {
    return '-';
  }
  return formatDomainValue(metadata[key]);
}

export function formatStatus(status) {
  return STATUS_LABELS[status] || status || '-';
}

export function formatRisk(riskLevel) {
  return RISK_LABELS[riskLevel] || riskLevel || '-';
}

export function normalizeMetadata(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function normalizeList(value) {
  return Array.isArray(value) ? value : [];
}

export function statusTone(status) {
  switch (status) {
    case 'pending_triage':
    case 'reopened':
      return 'alert';
    case 'assigned':
    case 'in_review':
      return 'focus';
    case 'resolved':
    case 'dismissed':
      return 'resolved';
    default:
      return 'muted';
  }
}

export function riskTone(riskLevel) {
  switch (riskLevel) {
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    default:
      return 'none';
  }
}

export function defaultNextStatus(status) {
  switch (status) {
    case 'pending_triage':
      return 'assigned';
    case 'assigned':
      return 'in_review';
    case 'in_review':
      return 'resolved';
    case 'resolved':
    case 'dismissed':
      return 'reopened';
    default:
      return 'assigned';
  }
}

export function summarizeArtifactMeta(artifact) {
  const meta = normalizeMetadata(artifact?.meta_json);
  switch (artifact?.artifact_type) {
    case 'continuity_turn':
      return [formatDomainValue(meta.status), meta.resolved_by, meta.title ? trimText(meta.title, 72) : null]
        .filter((item) => item && item !== '-')
        .join(' / ');
    case 'report_context':
    case 'wearable_context':
    case 'icsn_context':
      return [formatDomainValue(meta.status), meta.report_id || meta.patient_id || meta.source]
        .filter((item) => item && item !== '-')
        .join(' / ');
    case 'evidence_snapshot':
      return [formatDomainValue(meta.evidence_mode), formatDomainValue(meta.evidence_relevance_status)]
        .filter((item) => item && item !== '-')
        .join(' / ');
    case 'blocked_answer_snapshot':
      return [meta.source_stage, meta.preview ? trimText(meta.preview, 72) : null]
        .filter(Boolean)
        .join(' / ');
    default:
      return Object.keys(meta).length ? trimText(JSON.stringify(meta), 96) : '-';
  }
}

export function extractSnapshotValue(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') {
    return {};
  }
  if (snapshot.snapshot && typeof snapshot.snapshot === 'object') {
    return snapshot.snapshot;
  }
  if (snapshot.payload && typeof snapshot.payload === 'object') {
    return snapshot.payload;
  }
  return snapshot;
}

export function filterReviewCases(items, query) {
  const normalized = String(query || '').trim().toLowerCase();
  if (!normalized) {
    return items;
  }
  return items.filter((item) => {
    const metadata = normalizeMetadata(item.metadata_json);
    const haystack = [
      item.case_no,
      item.case_id,
      item.dec_id,
      item.session_id,
      item.summary_text,
      item.status,
      item.assigned_to,
      metadata.reason,
      metadata.rag_status,
      metadata.question,
      metadata.question_preview,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalized);
  });
}

export function formatEventActor(event) {
  if (!event) {
    return '-';
  }
  return [formatDomainValue(event.actor_type), event.actor_id].filter((item) => item && item !== '-').join(' / ') || '-';
}

export function formatEventPayloadSummary(event) {
  const payload = normalizeMetadata(event?.payload_json);
  if (event?.event_type === 'created') {
    return [
      payload.intent ? `의도 ${formatDomainValue(payload.intent)}` : null,
      payload.reason ? `사유 ${formatDomainValue(payload.reason)}` : null,
      payload.rag_status ? `RAG ${formatDomainValue(payload.rag_status)}` : null,
      payload.rag_reason ? `RAG 사유 ${formatDomainValue(payload.rag_reason)}` : null,
    ].filter(Boolean).join(' / ');
  }
  if (event?.event_type === 'assigned') {
    return [
      payload.previous_assigned_to ? `이전 ${payload.previous_assigned_to}` : null,
      payload.assigned_to ? `담당 ${payload.assigned_to}` : null,
      payload.reason ? `사유 ${payload.reason}` : null,
    ].filter(Boolean).join(' / ');
  }
  if (event?.event_type === 'status_changed') {
    return [
      payload.previous_status ? `이전 ${formatStatus(payload.previous_status)}` : null,
      payload.status ? `현재 ${formatStatus(payload.status)}` : null,
      payload.reason ? `사유 ${payload.reason}` : null,
    ].filter(Boolean).join(' / ');
  }
  if (event?.event_type === 'action_recorded') {
    return [
      payload.action_type ? ACTION_LABELS[payload.action_type] || formatDomainValue(payload.action_type) : null,
      payload.action_summary?.memo_preview ? `메모 ${payload.action_summary.memo_preview}` : null,
    ].filter(Boolean).join(' / ');
  }
  if (event?.event_type === 'note_created') {
    return [
      payload.note_type ? `유형 ${formatDomainValue(payload.note_type)}` : null,
      payload.visibility ? `공개 ${formatDomainValue(payload.visibility)}` : null,
    ].filter(Boolean).join(' / ');
  }
  if (event?.event_type === 'assistant_draft_decision') {
    return [
      payload.decision ? `결정 ${formatDomainValue(payload.decision)}` : null,
      payload.draft_type ? `초안 ${formatDomainValue(payload.draft_type)}` : null,
      payload.reason ? `사유 ${payload.reason}` : null,
    ].filter(Boolean).join(' / ');
  }
  return Object.keys(payload).length ? trimText(JSON.stringify(payload), 140) : '';
}

export function formatSnapshotError(key, error) {
  const label = SNAPSHOT_ERROR_LABELS[key] || '스냅샷';
  const message = error?.message || String(error || '');
  if (/not found|404/i.test(message)) {
    return `${label}을 찾을 수 없습니다. 오래된 검증 케이스이거나 스냅샷 저장 전 생성된 케이스일 수 있습니다.`;
  }
  return `${label}을 불러오지 못했습니다. ${message}`.trim();
}

export function ReviewChip({ children, tone, type = 'tone' }) {
  const suffix = type === 'risk' ? `risk-${tone || 'none'}` : `tone-${tone || 'muted'}`;
  return <span className={`review-queue-chip ${suffix}`}>{children}</span>;
}

export function InlineMessage({ kind = 'status', children }) {
  if (!children) {
    return null;
  }
  return <div className={`review-queue-inline-${kind}`}>{children}</div>;
}

export function SectionTitle({ label, marker }) {
  return (
    <div className="review-queue-section-title">
      <span>{label}</span>
      {marker ? <strong>{marker}</strong> : null}
    </div>
  );
}
